import { CallTransformation } from 'app/model/dataflow/calltransformation';
import { IntegerDataFlowType, StringDataFlowType, BooleanDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { isTruthy } from 'app/model/dataflow/dataflow-expression-utilities';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { RestCallSettings } from 'app/model/dataflow/dataset-settings/restCall-settings';
import { DataFlowDSLAdapterRegistry, Metamodel, Owned, DataFlowParameterConfig } from 'shell-models-lib';
import { DataFlowAdapterUtils } from '../adapter.util';
import { ExpressionBasedColumnVisitorAdapterUtils } from '../expression-based-column-visitor-adapter.utils';
import { DataFlowExpressionUtils } from '../expression.util';
import { BaseFormatAdapter } from './base-format.adapter';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(RestCallSettings)
})
export class RestCallFormatAdapter extends BaseFormatAdapter<RestCallSettings> {
    public toTransformModel(
        owner: CallTransformation,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): RestCallSettings {
        const restSettings = Owned.create(RestCallSettings.MODEL, owner.source) as RestCallSettings;
        restSettings.initialize(undefined);

        if (configs.has('httpCompressionType')) {
            restSettings.httpCompressionType = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('httpCompressionType'),
                owner
            );
            handledConfigs.push('httpCompressionType');
        }
        if (configs.has('requestFormat')) {
            const formatSettings = configs
                .get('requestFormat')
                .slice(1, configs.get('requestFormat').length - 1)
                .split(', ');
            formatSettings.forEach((setting) => {
                const [settingName, settingValue] = setting.split(' -> ');
                switch (settingName) {
                    case `'type'`:
                        restSettings.restDataFormat = DataFlowExpressionUtils.extractStringConfigValue(settingValue) as
                            | 'json'
                            | 'xml';
                        break;
                    case `'outputDateFormat'`:
                        restSettings.dateFormat = DataFlowExpressionUtils.extractStringConfigValue(settingValue);
                        break;
                    case `'outputTimestampFormat'`:
                        restSettings.timestampFormat = DataFlowExpressionUtils.extractStringConfigValue(settingValue);
                        break;
                    default:
                }
            });
            handledConfigs.push('requestFormat');
        } else {
            restSettings.restDataFormat = undefined;
        }
        if (configs.has('rowRelativeUrl')) {
            const expression = configs.get('rowRelativeUrl');
            restSettings.rowRelativeUrl = DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, owner);
            handledConfigs.push('rowRelativeUrl');
        }
        if (configs.has('skipRowRelativeUrl')) {
            restSettings.skipRowRelativeUrl = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('skipRowRelativeUrl'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('skipRowRelativeUrl');
        }
        if (configs.has('addResponseCode')) {
            handledConfigs.push('addResponseCode');
        }

        if (configs.has('cache')) {
            restSettings.cache = true;
            handledConfigs.push('cache');
        }

        if (configs.has('columnTypeMap')) {
            const columnSettings = configs
                .get('columnTypeMap')
                .slice(1, configs.get('columnTypeMap').length - 1)
                .split(DataFlowAdapterUtils.MAP_ARRAY_SPLITTER);
            columnSettings.forEach((setting) => {
                const [_, bodyColumnTypeParameter] = setting
                    .split(DataFlowAdapterUtils.MAP_KEY_VALUE_SPLITTER)
                    .map((v) => v.trim());
                owner.bodyColumnTypeParameter = (bodyColumnTypeParameter.startsWith('\'') && bodyColumnTypeParameter.endsWith('\'')) ?
                    bodyColumnTypeParameter.substring(1, bodyColumnTypeParameter.length-1) : bodyColumnTypeParameter;
            });
            handledConfigs.push('columnTypeMap');
        }

        owner.includeStatus = restSettings.includeStatus;
        owner.includeBody = restSettings.includeBody;
        owner.includeHeader = restSettings.includeHeader;
        this._commonPropertiesToModel(owner, configs, handledConfigs, restSettings);
        return restSettings;
    }

    public setOutputColumns(
        owner: CallTransformation,
        restSettings: RestCallSettings,
        handledConfigs: string[]
    ): RestCallSettings {
        if (owner.includeStatus && owner.statusColumn) {
            restSettings.includeStatus = true;
            restSettings.responseStatusColName.expression = owner.statusColumn.name;
            handledConfigs.push('statusColumnName');
        } else {
            restSettings.includeStatus = false;
        }
        if (owner.includeBody && owner.staticBodyColumn) {
            restSettings.includeBody = true;
            restSettings.responseBodyColName.expression = owner.staticBodyColumn.name;
            handledConfigs.push('bodyColumnName');
        } else {
            restSettings.includeBody = false;
        }
        if (owner.includeHeader && owner.headerColumn) {
            restSettings.includeHeader = true;
            restSettings.responseHeaderColName.expression = owner.headerColumn.name;
            handledConfigs.push('headerColumnName');
        } else {
            restSettings.includeHeader = false;
        }
        return restSettings;
    }

    public async toSinkDSL(restSettings: RestCallSettings): Promise<string[]> {
        return this.toSourceDSL(restSettings);
    }

    public getColumnExpression(expression: string) {
        return ExpressionBasedColumnVisitorAdapterUtils.writeColumnExpressionToDsl(expression);
    }

    public async toSourceDSL(restSettings: RestCallSettings): Promise<string[]> {
        const props = [];

        props.push(...this._commonPropertiesToDsl(restSettings));

        if (restSettings.requestMethod.expression) {
            props.push(
                `httpMethod: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    restSettings.requestMethod
                ).toUpperCase()}`
            );
        }

        if (restSettings.rowRelativeUrl.expression) {
            props.push(
                `rowRelativeUrl: ${DataFlowExpressionUtils.wrapExpressionConfigValue(restSettings.rowRelativeUrl)}`
            );
        }

        if (restSettings.requestEntity.expression) {
            props.push(`entity: ${DataFlowExpressionUtils.wrapExpressionConfigValue(restSettings.requestEntity)}`);
        }

        if (isTruthy(restSettings.skipRowRelativeUrl)) {
            props.push(
                `skipRowRelativeUrl: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    restSettings.skipRowRelativeUrl,
                    BooleanDataFlowType.TYPE
                )}`
            );
        }

        if (restSettings.includeHeader) {
            props.push(
                `headerColumnName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    restSettings.responseHeaderColName
                )}`
            );
        }

        if (restSettings.includeBody) {
            props.push(
                `bodyColumnName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(restSettings.responseBodyColName)}`
            );
        }

        if (restSettings.includeStatus) {
            props.push(
                `statusColumnName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    restSettings.responseStatusColName
                )}`
            );
            props.push(`addResponseCode: true`);
        }

        if (restSettings.cache) {
            props.push(`cache: true`);
        }

        let paginationRules = '';
        if (restSettings.paginationRules && restSettings.paginationRules.length) {
            if (paginationRules) {
                paginationRules += ', ';
            }
            paginationRules += restSettings.paginationRules
                .filter((rule) => rule.name && rule.value)
                .map((rule) => {
                    const key = this._replaceDoubleQuotes(
                        this.getColumnExpression((rule.paramName as DataFlowExpression).expression).trim()
                    );
                    const value = this._replaceDoubleQuotes(
                        this.getColumnExpression((rule.paramValue as DataFlowExpression).expression).trim()
                    );
                    return `${key} -> ${value}`;
                })
                .join(', ');
        }
        if (paginationRules) {
            props.push(`paginationRules = [${paginationRules}]`);
        }

        const isImportSchema = (restSettings.owner.owner as CallTransformation).isImportSchema;
        if (restSettings.restDataFormat === 'json') {
            const formatSettings = [`'type' -> 'json'`];
            if (!isImportSchema && restSettings.dateFormat) {
                formatSettings.push(`'outputDateFormat' -> '${restSettings.dateFormat}'`);
            }
            if (!isImportSchema && restSettings.timestampFormat) {
                formatSettings.push(`'outputTimestampFormat' -> '${restSettings.timestampFormat}'`);
            }
            const formatSettingsInDsl = formatSettings.join(', ');
            if (formatSettingsInDsl) {
                props.push(`requestFormat: [${formatSettingsInDsl}]`);
            }
        }

        if (restSettings.restResponseDataFormat) {
            if (restSettings.restResponseDataFormat === 'json') {
                const formatSettings = [`'type' -> 'json'`];
                formatSettings.push(
                    `'documentForm' -> ${DataFlowExpressionUtils.wrapExpressionConfigValue(restSettings.documentForm)}`
                );
                const formatSettingsInDsl = formatSettings.join(', ');
                if (formatSettingsInDsl) {
                    props.push(`responseFormat: [${formatSettingsInDsl}]`);
                }
            } else if (restSettings.restResponseDataFormat === 'xml') {
                const formatSettings = [`'type' -> 'xml'`];
                if (restSettings.nullValue) {
                    formatSettings.push(`'nullValue' -> '${restSettings.nullValue}'`);
                }
                formatSettings.push(`'namespaces' -> ${restSettings.namespaces}`);
                if (restSettings.namespaces) {
                    const namespacePrefixes = restSettings.namespacePrefixes
                        .filter((namespacePrefix) => namespacePrefix.key && namespacePrefix.val)
                        .map((namespacePrefix) => {
                            const namespacePrefixKey = DataFlowExpressionUtils.wrapExpressionConfigValue(
                                (restSettings.owner as DataFlowSource).createTemplatableExpression(
                                    StringDataFlowType.TYPE,
                                    namespacePrefix.key
                                )
                            );
                            const namespacePrefixVal = DataFlowExpressionUtils.wrapExpressionConfigValue(
                                (restSettings.owner as DataFlowSink).createTemplatableExpression(
                                    StringDataFlowType.TYPE,
                                    namespacePrefix.val
                                )
                            );
                            return `${namespacePrefixKey}->${namespacePrefixVal}`;
                        })
                        .join(',');
                    if (namespacePrefixes.length > 0) {
                        formatSettings.push(`'namespacePrefixes' -> [${namespacePrefixes}]`);
                    }
                }
                const formatSettingsInDsl = formatSettings.join(', ');
                if (formatSettingsInDsl) {
                    props.push(`responseFormat: [${formatSettingsInDsl}]`);
                }
            }
        }

        if (restSettings.restDataFormat && restSettings.batchSize.expression) {
            props.push(
                `batchSize: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    restSettings.batchSize,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        if (restSettings.httpCompressionType.expression) {
            props.push(
                `httpCompressionType: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    restSettings.httpCompressionType
                )}`
            );
        }

        return props;
    }

    private _commonPropertiesToDsl(restSettings: RestCallSettings): string[] {
        const props = [`format: 'rest'`];
        props.push(`store: 'restservice'`);
        if (restSettings.requestTimeout.expression) {
            props.push(
                `timeout: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    restSettings.requestTimeout,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        if (restSettings.requestInterval.expression) {
            props.push(
                `requestInterval: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    restSettings.requestInterval,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }

        if (restSettings.additionalHeaders && restSettings.additionalHeaders.length) {
            const headers = restSettings.additionalHeaders
                .filter((header) => header.name && header.value)
                .map((header) => {
                    const key = this._replaceDoubleQuotes(
                        this.getColumnExpression((header.paramName as DataFlowExpression).expression).trim()
                    );
                    const value = this._replaceDoubleQuotes(
                        this.getColumnExpression((header.paramValue as DataFlowExpression).expression).trim()
                    );
                    return `${key} -> ${value}`;
                })
                .join(', ');
            if (headers) {
                props.push(`headers = [${headers}]`);
            }
        }
        if (restSettings.queryParameters && restSettings.queryParameters.length) {
            const queryParameters = restSettings.queryParameters
                .filter((p) => p.name)
                .map((p) => {
                    const key = this._replaceDoubleQuotes(
                        this.getColumnExpression((p.paramName as DataFlowExpression).expression).trim()
                    );
                    const value = this._replaceDoubleQuotes(
                        this.getColumnExpression((p.paramValue as DataFlowExpression).expression).trim()
                    );
                    return `${key} -> ${value}`;
                })
                .join(', ');
            if (queryParameters) {
                props.push(`queryParameters = [${queryParameters}]`);
            }
        }
        return props;
    }

    private _replaceDoubleQuotes(expression: string): string {
        if (expression && expression.startsWith('"') && expression.endsWith('"')) {
            expression = `\'${expression.slice(1, expression.length - 1)}\'`;
        }
        return expression;
    }

    private _commonPropertiesToModel(
        owner: CallTransformation,
        configs: Map<string, string>,
        handledConfigs: string[],
        restSettings: RestCallSettings
    ): void {
        if (configs.has('httpMethod')) {
            restSettings.requestMethod = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('httpMethod'),
                owner
            );
            handledConfigs.push('httpMethod');
        }

        if (configs.has('timeout')) {
            restSettings.requestTimeout = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('timeout'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('timeout');
        }
        if (configs.has('requestInterval')) {
            restSettings.requestInterval = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('requestInterval'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('requestInterval');
        }
        if (configs.has('entity')) {
            restSettings.requestEntity = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('entity'),
                owner
            );
            handledConfigs.push('entity');
        }
        if (configs.has('headers')) {
            restSettings.additionalHeaders = configs
                .get('headers')
                .slice(1, configs.get('headers').length - 1)
                .split(DataFlowAdapterUtils.REST_CALL_SPLITTER)
                .map((header) => {
                    const splits = header.split(DataFlowAdapterUtils.MAP_KEY_VALUE_SPLITTER);
                    const dataFlowParameterConfig = new DataFlowParameterConfig();
                    dataFlowParameterConfig.paramName = new DataFlowExpression();
                    dataFlowParameterConfig.paramName.expression = splits[0].trim();
                    dataFlowParameterConfig.paramValue = new DataFlowExpression();
                    dataFlowParameterConfig.paramValue.expression = splits[1].trim();
                    return dataFlowParameterConfig;
                });
            handledConfigs.push('headers');
        }
        if (configs.has('queryParameters')) {
            restSettings.queryParameters = configs
                .get('queryParameters')
                .slice(1, configs.get('queryParameters').length - 1)
                .split(DataFlowAdapterUtils.REST_CALL_SPLITTER)
                .map((para) => {
                    const splits = para.split(DataFlowAdapterUtils.MAP_KEY_VALUE_SPLITTER);
                    const dataFlowParameterConfig = new DataFlowParameterConfig();
                    dataFlowParameterConfig.paramName = new DataFlowExpression();
                    dataFlowParameterConfig.paramName.expression = splits[0].trim();
                    dataFlowParameterConfig.paramValue = new DataFlowExpression();
                    dataFlowParameterConfig.paramValue.expression = splits[1].trim();
                    return dataFlowParameterConfig;
                });
            handledConfigs.push('queryParameters');
        }
        if (configs.has('responseFormat')) {
            const formatSettings = configs
                .get('responseFormat')
                .slice(1, configs.get('responseFormat').length - 1)
                .split(DataFlowAdapterUtils.MAP_ARRAY_SPLITTER);
            formatSettings.forEach((setting) => {
                const [settingName, settingValue] = setting
                    .split(DataFlowAdapterUtils.MAP_KEY_VALUE_SPLITTER)
                    .map((v) => v.trim());
                switch (settingName) {
                    case `'type'`:
                        restSettings.restResponseDataFormat = DataFlowExpressionUtils.extractStringConfigValue(
                            settingValue
                        ) as 'xml' | 'json';
                        break;
                    case `'documentForm'`:
                        restSettings.documentForm = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                            settingValue,
                            owner
                        );
                        break;
                    case `'nullValue'`:
                        restSettings.nullValue = DataFlowExpressionUtils.extractStringConfigValue(settingValue);
                        break;
                    case `'namespaces'`:
                        restSettings.namespaces = settingValue === 'true';
                        break;
                    case `'outputDateFormat'`:
                        restSettings.dateFormat = DataFlowExpressionUtils.extractStringConfigValue(settingValue);
                        break;
                    case `'outputTimestampFormat'`:
                        restSettings.timestampFormat = DataFlowExpressionUtils.extractStringConfigValue(settingValue);
                        break;
                    // TODO: Fix namespace prefixes
                    default:
                }
            });
            handledConfigs.push('responseFormat');
        } else {
            restSettings.restResponseDataFormat = undefined;
        }
        if (configs.has('paginationRules')) {
            restSettings.paginationRules = configs
                .get('paginationRules')
                .slice(1, configs.get('paginationRules').length - 1)
                .split(DataFlowAdapterUtils.REST_CALL_SPLITTER)
                .map((rule) => {
                    const splits = rule.split(DataFlowAdapterUtils.MAP_KEY_VALUE_SPLITTER);
                    if (splits[0].trim() === `'supportRFC5988'`) {
                        return undefined;
                    } else {
                        const dataFlowParameterConfig = new DataFlowParameterConfig();
                        dataFlowParameterConfig.paramName = new DataFlowExpression();
                        dataFlowParameterConfig.paramName.expression = splits[0].trim();
                        dataFlowParameterConfig.paramValue = new DataFlowExpression();
                        dataFlowParameterConfig.paramValue.expression = splits[1].trim();
                        return dataFlowParameterConfig;
                    }
                });
            restSettings.paginationRules = restSettings.paginationRules.filter((rule) => !!rule);
            handledConfigs.push('paginationRules');
        }
    }
}
